<template>
  <div class="rn">
    <aside class="rn-rail">
      <div class="rn-rail-head">
        <div>
          <p class="rn-kicker">Vault</p>
          <h3 class="rn-rail-title">Notes</h3>
        </div>
        <button type="button" class="rn-icon-btn" title="New note" @click="createNote">
          <i class="pi pi-plus" aria-hidden="true" />
        </button>
      </div>

      <div class="rn-search">
        <i class="pi pi-search" aria-hidden="true" />
        <input v-model="query" type="search" placeholder="Search notes…" />
      </div>

      <div class="rn-mode">
        <button type="button" class="rn-mode-btn" :class="{ 'is-on': view === 'editor' }" @click="view = 'editor'">
          Editor
        </button>
        <button type="button" class="rn-mode-btn" :class="{ 'is-on': view === 'graph' }" @click="view = 'graph'">
          Graph
        </button>
      </div>

      <ul class="rn-list">
        <li v-for="note in filteredNotes" :key="note.id">
          <button
            type="button"
            class="rn-list-item"
            :class="{ 'is-active': activeId === note.id }"
            @click="selectNote(note.id)"
          >
            <span class="rn-list-title">{{ note.title }}</span>
            <span class="rn-list-meta">
              <span v-if="(note.links || []).length" class="rn-list-links">{{ note.links.length }} links</span>
              <span>{{ relativeTime(note.updatedAt) }}</span>
            </span>
          </button>
        </li>
        <li v-if="!filteredNotes.length" class="rn-empty-rail">No notes match.</li>
      </ul>
    </aside>

    <section v-if="view === 'editor'" class="rn-main">
      <div v-if="!activeNote" class="rn-blank">
        <div class="rn-blank-card">
          <p class="rn-kicker">Empty page</p>
          <h3>Start a thought</h3>
          <p>
            Questions, decisions, and Mermaid flowcharts —
            link notes with <code>[[Title]]</code>.
          </p>
          <div class="rn-blank-actions">
            <button type="button" class="rn-primary" @click="createNote">New note</button>
            <button type="button" class="rn-flow-btn" @click="createNoteWithFlow">New note + flowchart</button>
          </div>
        </div>
      </div>

      <template v-else>
        <header class="rn-editor-bar">
          <input
            v-model="draft.title"
            class="rn-title-input"
            placeholder="Note title"
            @input="markDirty"
          />
          <div class="rn-editor-actions">
            <button type="button" class="rn-flow-btn" title="Insert Mermaid flowchart" @click="insertFlowchart">
              <i class="pi pi-sitemap" aria-hidden="true" />
              Flowchart
            </button>
            <button type="button" class="rn-flow-btn rn-flow-btn--ghost" title="Insert sequence diagram" @click="insertSequence">
              Sequence
            </button>
            <span class="rn-save-state" :class="{ 'is-dirty': dirty, 'is-saved': !dirty && justSaved }">
              {{ dirty ? 'Unsaved' : (justSaved ? 'Saved' : 'Up to date') }}
            </span>
            <button type="button" class="rn-ghost" :disabled="!dirty || saving" @click="save">
              {{ saving ? 'Saving…' : 'Save' }}
            </button>
            <button type="button" class="rn-ghost rn-danger" :disabled="deleting" @click="remove">
              Delete
            </button>
          </div>
        </header>

        <div class="rn-split">
          <div class="rn-write">
            <div class="rn-toolbar">
              <button type="button" class="rn-chip" @click="insertSnippet('## ')">H2</button>
              <button type="button" class="rn-chip" @click="insertSnippet('- ')">List</button>
              <button type="button" class="rn-chip" @click="insertSnippet('[[')">[[ link</button>
              <button type="button" class="rn-chip" @click="insertSnippet('## Questions\\n\\n- ')">Questions</button>
              <button type="button" class="rn-chip" @click="insertSnippet('## Decision\\n\\n')">Decision</button>
              <button type="button" class="rn-chip rn-chip-flow" @click="insertFlowchart">+ Flowchart</button>
              <button type="button" class="rn-chip" @click="insertSequence">+ Sequence</button>
            </div>
            <textarea
              ref="body"
              v-model="draft.body"
              class="rn-body"
              spellcheck="true"
              placeholder="Markdown + [[links]] + ```mermaid flowcharts"
              @input="markDirty"
              @keydown="onKeydown"
            />
          </div>

          <div class="rn-preview-pane">
            <div class="rn-preview-head">
              <span>Preview</span>
              <span class="rn-preview-hint">[[links]] · Mermaid flowcharts</span>
            </div>
            <div ref="preview" class="rn-preview" v-html="previewHtml" @click="onPreviewClick" />

            <div class="rn-backlinks">
              <h4>Backlinks</h4>
              <ul v-if="backlinks.length">
                <li v-for="bl in backlinks" :key="bl.id">
                  <button type="button" @click="selectNote(bl.id)">{{ bl.title }}</button>
                </li>
              </ul>
              <p v-else class="rn-muted">Nothing points here yet.</p>

              <h4>Outgoing</h4>
              <ul v-if="outgoing.length">
                <li v-for="link in outgoing" :key="link.title">
                  <button
                    type="button"
                    :class="{ 'is-missing': !link.id }"
                    @click="link.id ? selectNote(link.id) : createLinked(link.title)"
                  >
                    {{ link.title }}
                    <span v-if="!link.id">create</span>
                  </button>
                </li>
              </ul>
              <p v-else class="rn-muted">No [[links]] in this note.</p>
            </div>
          </div>
        </div>
      </template>
    </section>

    <section v-else class="rn-graph-wrap">
      <header class="rn-graph-head">
        <div>
          <p class="rn-kicker">Knowledge map</p>
          <h3>How notes connect</h3>
        </div>
        <p class="rn-muted">{{ graph.nodes.length }} notes · {{ graph.edges.length }} links</p>
      </header>
      <div class="rn-graph-stage" ref="graphStage">
        <svg class="rn-graph-svg" :viewBox="'0 0 ' + graphW + ' ' + graphH" preserveAspectRatio="xMidYMid meet">
          <defs>
            <marker id="rn-arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="5" markerHeight="5" orient="auto-start-reverse">
              <path d="M 0 0 L 10 5 L 0 10 z" fill="#64748b" />
            </marker>
          </defs>
          <line
            v-for="(e, i) in graph.edges"
            :key="'e'+i"
            :x1="e.x1"
            :y1="e.y1"
            :x2="e.x2"
            :y2="e.y2"
            class="rn-edge"
            marker-end="url(#rn-arrow)"
          />
          <g
            v-for="n in graph.nodes"
            :key="n.id"
            class="rn-node"
            :class="{ 'is-active': n.id === activeId }"
            @click="selectNote(n.id); view = 'editor'"
          >
            <circle :cx="n.x" :cy="n.y" :r="n.r" />
            <text :x="n.x" :y="n.y + n.r + 14" text-anchor="middle">{{ n.label }}</text>
          </g>
        </svg>
        <p v-if="!graph.nodes.length" class="rn-graph-empty">Create notes and link them with [[Title]].</p>
      </div>
    </section>
  </div>
</template>

<script>
import gql from 'graphql-tag'

const LIST_QUERY = gql`
  query RoadmapNotes {
    roadmapNotes {
      id
      title
      body
      links
      updatedAt
      updatedBy
      createdAt
    }
  }
`

const SAVE_MUTATION = gql`
  mutation SaveRoadmapNote($input: RoadmapNoteInput!) {
    saveRoadmapNote(input: $input) {
      id
      title
      body
      links
      updatedAt
      updatedBy
      createdAt
    }
  }
`

const DELETE_MUTATION = gql`
  mutation DeleteRoadmapNote($id: ID!) {
    deleteRoadmapNote(id: $id)
  }
`

const WIKI_RE = /\[\[([^\]|#]+)(?:\|([^\]]+))?\]\]/g
const MERMAID_FENCE_RE = /```mermaid\s*\n([\s\S]*?)```/gi

const FLOWCHART_SNIPPET = [
  '```mermaid',
  'flowchart TD',
  '  A[Start] --> B{Decision?}',
  '  B -->|Yes| C[Ship it]',
  '  B -->|No| D[More checks]',
  '  D --> B',
  '  C --> E[Done]',
  '```',
  '',
].join('\n')

const SEQUENCE_SNIPPET = [
  '```mermaid',
  'sequenceDiagram',
  '  participant Op as Operator',
  '  participant UI as Brokurz',
  '  participant MLS as MLS feed',
  '  Op->>UI: Connect source',
  '  UI->>MLS: Sync',
  '  MLS-->>UI: Listings',
  '  UI-->>Op: Green status',
  '```',
  '',
].join('\n')

let mermaidLoader = null
function loadMermaidFromCdn() {
  if (typeof window !== 'undefined' && window.mermaid) {
    return Promise.resolve(window.mermaid)
  }
  if (mermaidLoader) return mermaidLoader
  mermaidLoader = new Promise((resolve, reject) => {
    const existing = document.querySelector('script[data-rn-mermaid]')
    if (existing) {
      existing.addEventListener('load', () => resolve(window.mermaid))
      existing.addEventListener('error', reject)
      return
    }
    const s = document.createElement('script')
    s.src = 'https://cdn.jsdelivr.net/npm/mermaid@9.4.3/dist/mermaid.min.js'
    s.async = true
    s.setAttribute('data-rn-mermaid', '1')
    s.onload = () => resolve(window.mermaid)
    s.onerror = () => reject(new Error('Failed to load Mermaid'))
    document.head.appendChild(s)
  })
  return mermaidLoader
}

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function renderMarkdownChunk(chunk) {
  const lines = String(chunk || '').split('\n')
  const html = []
  let inList = false
  const flushList = () => {
    if (inList) {
      html.push('</ul>')
      inList = false
    }
  }
  const wikiify = (text) => escapeHtml(text).replace(WIKI_RE, (_, title, alias) => {
    const label = escapeHtml((alias || title).trim())
    const target = escapeHtml(title.trim())
    return `<button type="button" class="rn-wikilink" data-wiki="${target}">${label}</button>`
  })

  lines.forEach((line) => {
    if (/^###\s+/.test(line)) {
      flushList()
      html.push(`<h4>${wikiify(line.replace(/^###\s+/, ''))}</h4>`)
      return
    }
    if (/^##\s+/.test(line)) {
      flushList()
      html.push(`<h3>${wikiify(line.replace(/^##\s+/, ''))}</h3>`)
      return
    }
    if (/^#\s+/.test(line)) {
      flushList()
      html.push(`<h2>${wikiify(line.replace(/^#\s+/, ''))}</h2>`)
      return
    }
    if (/^[-*]\s+/.test(line)) {
      if (!inList) {
        html.push('<ul>')
        inList = true
      }
      html.push(`<li>${wikiify(line.replace(/^[-*]\s+/, ''))}</li>`)
      return
    }
    if (!line.trim()) {
      flushList()
      html.push('<div class="rn-gap"></div>')
      return
    }
    flushList()
    html.push(`<p>${wikiify(line)}</p>`)
  })
  flushList()
  return html.join('')
}

function renderPreview(body) {
  const text = String(body || '')
  const parts = []
  let last = 0
  let m
  let mermaidCount = 0
  MERMAID_FENCE_RE.lastIndex = 0
  while ((m = MERMAID_FENCE_RE.exec(text)) !== null) {
    if (m.index > last) {
      parts.push(renderMarkdownChunk(text.slice(last, m.index)))
    }
    mermaidCount += 1
    const code = m[1].trim()
    parts.push(
      `<div class="rn-mermaid-wrap">`
      + `<div class="rn-mermaid-label">Flowchart</div>`
      + `<pre class="rn-mermaid" data-mermaid-i="${mermaidCount}">${escapeHtml(code)}</pre>`
      + `</div>`
    )
    last = m.index + m[0].length
  }
  if (last < text.length) {
    parts.push(renderMarkdownChunk(text.slice(last)))
  }
  return parts.join('') || renderMarkdownChunk('')
}

function extractLinkTitles(body) {
  const titles = []
  const seen = new Set()
  let m
  WIKI_RE.lastIndex = 0
  while ((m = WIKI_RE.exec(String(body || ''))) !== null) {
    const t = String(m[1] || '').trim()
    const key = t.toLowerCase()
    if (!t || seen.has(key)) continue
    seen.add(key)
    titles.push(t)
  }
  return titles
}

export default {
  data() {
    return {
      notes: [],
      activeId: null,
      query: '',
      view: 'editor',
      draft: { title: '', body: '' },
      dirty: false,
      saving: false,
      deleting: false,
      justSaved: false,
      graphW: 900,
      graphH: 520,
      saveTimer: null,
      mermaidTimer: null,
      mermaidReady: false,
    }
  },
  computed: {
    filteredNotes() {
      const q = this.query.trim().toLowerCase()
      if (!q) return this.notes
      return this.notes.filter(n =>
        (n.title || '').toLowerCase().includes(q)
        || (n.body || '').toLowerCase().includes(q)
      )
    },
    activeNote() {
      return this.notes.find(n => n.id === this.activeId) || null
    },
    previewHtml() {
      return renderPreview(this.draft.body)
    },
    outgoing() {
      return extractLinkTitles(this.draft.body).map((title) => {
        const found = this.findByTitle(title)
        return { title, id: found ? found.id : null }
      })
    },
    backlinks() {
      if (!this.activeNote) return []
      const key = (this.draft.title || this.activeNote.title || '').trim().toLowerCase()
      if (!key) return []
      return this.notes.filter((n) => {
        if (n.id === this.activeId) return false
        return (n.links || []).some(l => String(l).toLowerCase() === key)
          || extractLinkTitles(n.body).some(t => t.toLowerCase() === key)
      })
    },
    graph() {
      const notes = this.notes
      if (!notes.length) return { nodes: [], edges: [] }
      const w = this.graphW
      const h = this.graphH
      const cx = w / 2
      const cy = h / 2
      const radius = Math.min(w, h) * 0.32
      const byTitle = {}
      notes.forEach((n) => { byTitle[n.title.toLowerCase()] = n.id })

      const nodes = notes.map((n, i) => {
        const angle = (Math.PI * 2 * i) / notes.length - Math.PI / 2
        const linkCount = (n.links || []).length + this.notes.filter(o =>
          (o.links || []).some(l => String(l).toLowerCase() === n.title.toLowerCase())
        ).length
        return {
          id: n.id,
          label: n.title.length > 22 ? `${n.title.slice(0, 20)}…` : n.title,
          x: cx + Math.cos(angle) * radius,
          y: cy + Math.sin(angle) * radius,
          r: 12 + Math.min(10, linkCount * 2),
        }
      })
      const pos = {}
      nodes.forEach((n) => { pos[n.id] = n })

      const edges = []
      const seen = new Set()
      notes.forEach((n) => {
        extractLinkTitles(n.body).forEach((title) => {
          const tid = byTitle[title.toLowerCase()]
          if (!tid || tid === n.id) return
          const key = [n.id, tid].sort().join('>')
          if (seen.has(key)) return
          seen.add(key)
          const a = pos[n.id]
          const b = pos[tid]
          if (!a || !b) return
          edges.push({ x1: a.x, y1: a.y, x2: b.x, y2: b.y })
        })
      })
      return { nodes, edges }
    },
  },
  mounted() {
    this.load()
    window.addEventListener('resize', this.measureGraph)
    this.$nextTick(() => {
      this.measureGraph()
      this.renderMermaid()
    })
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.measureGraph)
    if (this.saveTimer) clearTimeout(this.saveTimer)
    if (this.mermaidTimer) clearTimeout(this.mermaidTimer)
  },
  watch: {
    view(v) {
      if (v === 'graph') this.$nextTick(this.measureGraph)
      if (v === 'editor') this.$nextTick(this.renderMermaid)
    },
    previewHtml() {
      if (this.mermaidTimer) clearTimeout(this.mermaidTimer)
      this.mermaidTimer = setTimeout(() => this.renderMermaid(), 280)
    },
  },
  methods: {
    findByTitle(title) {
      const key = String(title || '').trim().toLowerCase()
      return this.notes.find(n => n.title.toLowerCase() === key) || null
    },
    async renderMermaid() {
      const root = this.$refs.preview
      if (!root || this.view !== 'editor') return
      const nodes = root.querySelectorAll('pre.rn-mermaid')
      if (!nodes.length) return
      try {
        const mermaid = await loadMermaidFromCdn()
        if (!this.mermaidReady) {
          mermaid.initialize({
            startOnLoad: false,
            theme: 'neutral',
            securityLevel: 'strict',
            flowchart: { curve: 'basis', htmlLabels: true },
          })
          this.mermaidReady = true
        }
        for (let i = 0; i < nodes.length; i += 1) {
          const el = nodes[i]
          if (el.getAttribute('data-rendered') === '1') continue
          const code = el.textContent || ''
          if (!code.trim()) continue
          const id = `rn-mmd-${Date.now()}-${i}`
          try {
            // mermaid 9 CDN: render(id, text, cb) or promise-based depending on build
            const result = mermaid.render(id, code)
            if (result && typeof result.then === 'function') {
              const { svg } = await result
              const host = document.createElement('div')
              host.className = 'rn-mermaid-svg'
              host.innerHTML = svg
              el.replaceWith(host)
            } else if (typeof result === 'string') {
              const host = document.createElement('div')
              host.className = 'rn-mermaid-svg'
              host.innerHTML = result
              el.replaceWith(host)
            } else {
              await new Promise((resolve, reject) => {
                mermaid.render(id, code, (svgCode) => {
                  try {
                    const host = document.createElement('div')
                    host.className = 'rn-mermaid-svg'
                    host.innerHTML = svgCode
                    el.replaceWith(host)
                    resolve()
                  } catch (e) {
                    reject(e)
                  }
                })
              })
            }
          } catch (err) {
            el.classList.add('is-error')
            el.setAttribute('data-rendered', '1')
            el.setAttribute('title', (err && err.message) || 'Mermaid error')
          }
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[notes] mermaid', err)
      }
    },
    insertFlowchart() {
      this.insertSnippet(FLOWCHART_SNIPPET)
    },
    insertSequence() {
      this.insertSnippet(SEQUENCE_SNIPPET)
    },
    async createNoteWithFlow() {
      await this.createNote()
      this.$nextTick(() => {
        if (!(this.draft.body || '').includes('```mermaid')) {
          this.draft.body = `${this.draft.body || ''}\n${FLOWCHART_SNIPPET}`
          this.markDirty()
        }
      })
    },
    relativeTime(iso) {
      if (!iso) return ''
      const t = new Date(iso).getTime()
      if (Number.isNaN(t)) return ''
      const sec = Math.max(0, Math.round((Date.now() - t) / 1000))
      if (sec < 60) return 'just now'
      if (sec < 3600) return `${Math.floor(sec / 60)}m`
      if (sec < 86400) return `${Math.floor(sec / 3600)}h`
      return `${Math.floor(sec / 86400)}d`
    },
    measureGraph() {
      const el = this.$refs.graphStage
      if (!el) return
      const rect = el.getBoundingClientRect()
      this.graphW = Math.max(640, Math.floor(rect.width) || 900)
      this.graphH = Math.max(420, Math.floor(rect.height) || 520)
    },
    markDirty() {
      this.dirty = true
      this.justSaved = false
      if (this.saveTimer) clearTimeout(this.saveTimer)
      this.saveTimer = setTimeout(() => {
        if (this.dirty) this.save()
      }, 1200)
    },
    selectNote(id) {
      if (this.dirty && this.activeId && this.activeId !== id) {
        this.save()
      }
      const note = this.notes.find(n => n.id === id)
      if (!note) return
      this.activeId = id
      this.draft = { title: note.title, body: note.body || '' }
      this.dirty = false
      this.view = 'editor'
    },
    async load() {
      try {
        const { data } = await this.$apollo.query({
          query: LIST_QUERY,
          fetchPolicy: 'network-only',
        })
        this.notes = data.roadmapNotes || []
        if (!this.activeId && this.notes.length) {
          this.selectNote(this.notes[0].id)
        } else if (this.activeId) {
          const still = this.notes.find(n => n.id === this.activeId)
          if (still) {
            this.draft = { title: still.title, body: still.body || '' }
            this.dirty = false
          }
        }
      } catch (err) {
        this.$toast.add({ severity: 'error', summary: 'Notes failed', detail: err.message, life: 4000 })
      }
    },
    async createNote() {
      if (this.dirty) await this.save()
      this.saving = true
      try {
        const { data } = await this.$apollo.mutate({
          mutation: SAVE_MUTATION,
          variables: {
            input: {
              title: 'Untitled',
              body: '## Questions\n\n- \n\n## Notes\n\n',
            },
          },
        })
        const note = data.saveRoadmapNote
        this.notes = [note, ...this.notes.filter(n => n.id !== note.id)]
        this.selectNote(note.id)
        this.$nextTick(() => {
          if (this.$refs.body) this.$refs.body.focus()
        })
      } catch (err) {
        this.$toast.add({ severity: 'error', summary: 'Create failed', detail: err.message, life: 4000 })
      } finally {
        this.saving = false
      }
    },
    async createLinked(title) {
      this.saving = true
      try {
        const { data } = await this.$apollo.mutate({
          mutation: SAVE_MUTATION,
          variables: {
            input: {
              title,
              body: `## ${title}\n\nLinked from [[${this.draft.title || 'Untitled'}]].\n\n## Questions\n\n- \n`,
            },
          },
        })
        const note = data.saveRoadmapNote
        this.notes = [note, ...this.notes.filter(n => n.id !== note.id)]
        await this.save()
        this.selectNote(note.id)
      } catch (err) {
        this.$toast.add({ severity: 'error', summary: 'Create failed', detail: err.message, life: 4000 })
      } finally {
        this.saving = false
      }
    },
    async save() {
      if (!this.activeId) return
      this.saving = true
      try {
        const { data } = await this.$apollo.mutate({
          mutation: SAVE_MUTATION,
          variables: {
            input: {
              id: this.activeId,
              title: (this.draft.title || '').trim() || 'Untitled',
              body: this.draft.body,
            },
          },
        })
        const note = data.saveRoadmapNote
        this.notes = this.notes.map(n => (n.id === note.id ? note : n))
          .sort((a, b) => String(b.updatedAt || '').localeCompare(String(a.updatedAt || '')))
        this.draft = { title: note.title, body: note.body || '' }
        this.dirty = false
        this.justSaved = true
      } catch (err) {
        this.$toast.add({ severity: 'error', summary: 'Save failed', detail: err.message, life: 4000 })
      } finally {
        this.saving = false
      }
    },
    async remove() {
      if (!this.activeId) return
      const ok = window.confirm('Delete this note?')
      if (!ok) return
      this.deleting = true
      try {
        await this.$apollo.mutate({
          mutation: DELETE_MUTATION,
          variables: { id: this.activeId },
        })
        this.notes = this.notes.filter(n => n.id !== this.activeId)
        this.activeId = null
        this.draft = { title: '', body: '' }
        this.dirty = false
        if (this.notes[0]) this.selectNote(this.notes[0].id)
        this.$toast.add({ severity: 'success', summary: 'Note deleted', life: 2000 })
      } catch (err) {
        this.$toast.add({ severity: 'error', summary: 'Delete failed', detail: err.message, life: 4000 })
      } finally {
        this.deleting = false
      }
    },
    insertSnippet(raw) {
      const text = String(raw).replace(/\\n/g, '\n')
      const el = this.$refs.body
      if (!el) {
        this.draft.body += text
        this.markDirty()
        return
      }
      const start = el.selectionStart
      const end = el.selectionEnd
      const before = this.draft.body.slice(0, start)
      const after = this.draft.body.slice(end)
      this.draft.body = before + text + after
      this.markDirty()
      this.$nextTick(() => {
        el.focus()
        const pos = start + text.length
        el.setSelectionRange(pos, pos)
      })
    },
    onKeydown(e) {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault()
        this.save()
      }
    },
    onPreviewClick(e) {
      const btn = e.target.closest('.rn-wikilink')
      if (!btn) return
      const title = btn.getAttribute('data-wiki')
      const found = this.findByTitle(title)
      if (found) this.selectNote(found.id)
      else this.createLinked(title)
    },
  },
}
</script>

<style scoped>
.rn {
  display: grid;
  grid-template-columns: 15.5rem minmax(0, 1fr);
  gap: 0;
  min-height: 34rem;
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid #e4e4e7;
  background: #fff;
  }

.rn-rail {
  display: flex;
  flex-direction: column;
  border-right: 1px solid rgba(255, 255, 255, 0.08);
  background: #fafafa;
  min-height: 0;
}

.rn-rail-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.9rem 0.85rem 0.55rem;
}

.rn-kicker {
  margin: 0;
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #a1a1aa;
}

.rn-rail-title {
  margin: 0.1rem 0 0;
  font-size: 1rem;
  font-weight: 800;
  letter-spacing: -0.03em;
  color: #18181b;
}

.rn-icon-btn {
  width: 2rem;
  height: 2rem;
  border-radius: 8px;
  border: 1px solid #e4e4e7;
  background: #fff;
  color: #18181b;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.rn-icon-btn:hover {
  background: #f4f4f5;
}

.rn-search {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin: 0 0.75rem 0.55rem;
  padding: 0.4rem 0.55rem;
  border-radius: 8px;
  border: 1px solid #e4e4e7;
  background: #fff;
  color: #a1a1aa;
}

.rn-search input {
  flex: 1;
  border: 0;
  background: transparent;
  color: #18181b;
  font: inherit;
  font-size: 0.8rem;
  outline: none;
}

.rn-mode {
  display: flex;
  gap: 0.25rem;
  margin: 0 0.75rem 0.65rem;
  padding: 0.2rem;
  border-radius: 10px;
  background: #f4f4f5;
}

.rn-mode-btn {
  flex: 1;
  border: 0;
  border-radius: 8px;
  padding: 0.35rem 0.4rem;
  background: transparent;
  color: #71717a;
  font-size: 0.72rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
}

.rn-mode-btn.is-on {
  background: #18181b;
  color: #fff;
}

.rn-list {
  list-style: none;
  margin: 0;
  padding: 0 0.45rem 0.75rem;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}

.rn-list-item {
  width: 100%;
  text-align: left;
  border: 0;
  border-radius: 10px;
  padding: 0.55rem 0.6rem;
  background: transparent;
  color: #52525b;
  cursor: pointer;
  font-family: inherit;
  margin-bottom: 0.2rem;
}

.rn-list-item:hover {
  background: #f4f4f5;
}

.rn-list-item.is-active {
  background: #18181b;
  color: #fff;
}

.rn-list-title {
  display: block;
  font-size: 0.82rem;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.rn-list-meta {
  display: flex;
  justify-content: space-between;
  gap: 0.35rem;
  margin-top: 0.2rem;
  font-size: 0.65rem;
  color: #a1a1aa;
  font-weight: 600;
}

.rn-list-links {
  color: #15803d;
}

.rn-empty-rail {
  padding: 0.75rem;
  color: #a1a1aa;
  font-size: 0.78rem;
}

.rn-main,
.rn-graph-wrap {
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: #fff;
}

.rn-blank {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
}

.rn-blank-card {
  max-width: 22rem;
  text-align: center;
  color: #71717a;
}

.rn-blank-card h3 {
  margin: 0.35rem 0;
  color: #18181b;
  font-size: 1.25rem;
  letter-spacing: -0.03em;
}

.rn-blank-card code {
  color: #15803d;
  font-size: 0.85em;
}

.rn-primary {
  margin-top: 0.85rem;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.55rem 0.95rem;
  border: 0;
  border-radius: 999px;
  background: #18181b;
  color: #fff;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
}

.rn-editor-bar {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem 0.55rem;
  border-bottom: 1px solid #f4f4f5;
}

.rn-title-input {
  flex: 1;
  min-width: 0;
  border: 0;
  background: transparent;
  color: #18181b;
  font-size: 1.25rem;
  font-weight: 800;
  letter-spacing: -0.03em;
  font-family: inherit;
  outline: none;
}

.rn-editor-actions {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  flex-shrink: 0;
  flex-wrap: wrap;
}

.rn-flow-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  border: 1px solid #18181b;
  border-radius: 8px;
  padding: 0.4rem 0.7rem;
  background: #18181b;
  color: #fff;
  font-size: 0.75rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
}

.rn-flow-btn:hover {
  background: #09090b;
}

.rn-flow-btn--ghost {
  background: #fff;
  color: #18181b;
}

.rn-flow-btn--ghost:hover {
  background: #f4f4f5;
}

.rn-blank-actions {
  margin-top: 0.85rem;
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.rn-blank-actions .rn-primary {
  margin-top: 0;
}

.rn-save-state {
  font-size: 0.68rem;
  font-weight: 700;
  color: #a1a1aa;
}

.rn-save-state.is-dirty { color: #a16207; }
.rn-save-state.is-saved { color: #4ade80; }

.rn-ghost {
  border: 1px solid #e4e4e7;
  border-radius: 8px;
  padding: 0.4rem 0.7rem;
  background: #fff;
  color: #3f3f46;
  font-size: 0.75rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
}

.rn-ghost:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.rn-ghost:hover:not(:disabled) {
  background: #f4f4f5;
}

.rn-danger {
  color: #b91c1c;
  border-color: #fecaca;
}

.rn-split {
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: minmax(0, 1.05fr) minmax(0, 0.95fr);
}

.rn-write {
  display: flex;
  flex-direction: column;
  min-height: 0;
  border-right: 1px solid #f4f4f5;
}

.rn-toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
  padding: 0.55rem 0.85rem;
}

.rn-chip {
  border: 1px solid #e4e4e7;
  border-radius: 999px;
  padding: 0.25rem 0.55rem;
  background: #fff;
  color: #71717a;
  font-size: 0.68rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
}

.rn-chip:hover {
  color: #18181b;
  border-color: rgba(255, 255, 255, 0.22);
}

.rn-chip-flow {
  border-color: #18181b !important;
  color: #18181b !important;
  background: #f4f4f5 !important;
}

.rn-body {
  flex: 1;
  min-height: 18rem;
  width: 100%;
  border: 0;
  resize: none;
  padding: 0.35rem 1rem 1.25rem;
  background: transparent;
  color: #3f3f46;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.86rem;
  line-height: 1.65;
  outline: none;
}

.rn-preview-pane {
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: auto;
}

.rn-preview-head {
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.65rem 1rem 0.35rem;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #a1a1aa;
}

.rn-preview-hint {
  text-transform: none;
  letter-spacing: 0;
  font-weight: 600;
  color: #a1a1aa;
}

.rn-preview {
  padding: 0.35rem 1rem 1rem;
  color: #52525b;
  font-size: 0.9rem;
  line-height: 1.55;
}

.rn-preview >>> h2,
.rn-preview >>> h3,
.rn-preview >>> h4 {
  margin: 0.85rem 0 0.35rem;
  color: #18181b;
  letter-spacing: -0.02em;
}

.rn-preview >>> p {
  margin: 0.35rem 0;
}

.rn-preview >>> ul {
  margin: 0.35rem 0;
  padding-left: 1.15rem;
}

.rn-preview >>> .rn-gap {
  height: 0.55rem;
}

.rn-preview >>> .rn-wikilink {
  display: inline;
  border: 0;
  padding: 0;
  background: transparent;
  color: #15803d;
  font: inherit;
  font-weight: 700;
  text-decoration: underline;
  text-decoration-color: rgba(21, 128, 61, 0.35);
  cursor: pointer;
}

.rn-preview >>> .rn-wikilink:hover {
  color: #166534;
}

.rn-preview >>> .rn-mermaid-wrap {
  margin: 0.75rem 0 1rem;
  padding: 0.75rem;
  border: 1px solid #e4e4e7;
  border-radius: 12px;
  background: #fafafa;
}

.rn-preview >>> .rn-mermaid-label {
  margin: 0 0 0.5rem;
  font-size: 0.62rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #a1a1aa;
}

.rn-preview >>> .rn-mermaid {
  margin: 0;
  padding: 0.65rem;
  border-radius: 8px;
  background: #fff;
  border: 1px dashed #e4e4e7;
  color: #52525b;
  font-size: 0.75rem;
  white-space: pre-wrap;
  overflow-x: auto;
}

.rn-preview >>> .rn-mermaid.is-error {
  border-color: #fecaca;
  color: #b91c1c;
}

.rn-preview >>> .rn-mermaid-svg {
  overflow-x: auto;
  background: #fff;
  border-radius: 8px;
  padding: 0.5rem;
}

.rn-preview >>> .rn-mermaid-svg svg {
  max-width: 100%;
  height: auto;
}

.rn-backlinks {
  margin-top: auto;
  padding: 0.75rem 1rem 1rem;
  border-top: 1px solid #f4f4f5;
  background: #fafafa;
}

.rn-backlinks h4 {
  margin: 0.55rem 0 0.3rem;
  font-size: 0.68rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #a1a1aa;
}

.rn-backlinks ul {
  list-style: none;
  margin: 0;
  padding: 0;
}

.rn-backlinks button {
  display: flex;
  justify-content: space-between;
  width: 100%;
  border: 0;
  border-radius: 8px;
  padding: 0.35rem 0.45rem;
  background: transparent;
  color: #3f3f46;
  font-size: 0.8rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  text-align: left;
}

.rn-backlinks button:hover {
  background: #fff;
}

.rn-backlinks button.is-missing {
  color: #a16207;
}

.rn-muted {
  margin: 0;
  color: #a1a1aa;
  font-size: 0.78rem;
}

.rn-graph-head {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: flex-end;
  padding: 1rem 1.15rem 0.5rem;
}

.rn-graph-head h3 {
  margin: 0.15rem 0 0;
  color: #18181b;
  font-size: 1.15rem;
  letter-spacing: -0.03em;
}

.rn-graph-stage {
  position: relative;
  flex: 1;
  min-height: 26rem;
  margin: 0.5rem 1rem 1rem;
  border-radius: 14px;
  border: 1px solid #e4e4e7;
  background:
    radial-gradient(circle at 50% 45%, rgba(24, 24, 27, 0.04), transparent 55%),
    #fafafa;
  overflow: hidden;
}

.rn-graph-svg {
  width: 100%;
  height: 100%;
  display: block;
}

.rn-edge {
  stroke: #d4d4d8;
  stroke-width: 1.5;
  opacity: 0.75;
}

.rn-node {
  cursor: pointer;
}

.rn-node circle {
  fill: #18181b;
  stroke: #fff;
  stroke-width: 2;
  transition: fill 0.15s ease;
}

.rn-node.is-active circle,
.rn-node:hover circle {
  fill: #3f3f46;
}

.rn-node text {
  fill: #52525b;
  font-size: 11px;
  font-weight: 700;
  font-family: 'Public Sans', system-ui, sans-serif;
}

.rn-graph-empty {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  color: #a1a1aa;
  font-size: 0.9rem;
}

@media (max-width: 960px) {
  .rn {
    grid-template-columns: 1fr;
  }

  .rn-rail {
    border-right: 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    max-height: 14rem;
  }

  .rn-split {
    grid-template-columns: 1fr;
  }

  .rn-write {
    border-right: 0;
    border-bottom: 1px solid #f4f4f5;
  }
}
</style>
